import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/cn";
import { useFieldControlProps } from "@/components/Field";

/**
 * Password input that auto-wires to the surrounding <Field.Root>.
 * Includes a show/hide toggle that flips the input's `type`.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "type">
>(({ className, ...props }, ref) => {
  const wired = useFieldControlProps();
  const [visible, setVisible] = useState(false);
  return (
    <div
      className={cn(
        "group relative flex h-10 w-full items-center rounded-[var(--radius-md)] border border-border-default bg-surface",
        "transition-[border-color,box-shadow] duration-[var(--duration-micro)] ease-[var(--ease-out)]",
        "hover:border-border-strong",
        "focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-border-focus",
        "aria-[invalid=true]:border-danger-fg",
        className,
      )}
      aria-invalid={wired["aria-invalid"]}
    >
      <input
        ref={ref}
        type={visible ? "text" : "password"}
        {...wired}
        {...props}
        className="min-w-0 flex-1 rounded-[var(--radius-md)] bg-transparent px-3 text-sm text-fg outline-none placeholder:text-fg-subtle"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        className="mr-1 inline-flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-subtle hover:bg-surface-hover hover:text-fg"
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";
